

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiBase() {
    return window.location.href.substr(0, window.location.href.lastIndexOf("/") + 1);
}

function formBody(data) {
    const fd = new FormData();
    fd.append('request', typeof data === 'string' ? data : JSON.stringify(data));
    return fd;
}

/** Extract all quote/building fields from a v5 ApplicationFull object into localStorage. */
function storeQuoteInfo(app) {
    const building = app?.lines?.[0]?.risks?.[0]?.building;
    const riskAddr = building?.addresses?.find(a => a.addrTypeCd === 'RiskLookupAddr');

    window.localStorage.setItem("quote_number", app.applicationNumber);
    window.localStorage.setItem("quote_system_id", app.systemId?.toString());

    if (riskAddr) {
        window.localStorage.setItem("agency_information", JSON.stringify({
            agency_name: riskAddr.countyAgencyName,
            agency_phone: riskAddr.countyAgencyPhoneNumber,
            agency_email: riskAddr.countyAgencyEmail,
            agency_addres: riskAddr.countyAgencyAddress,
            agency_city: riskAddr.countyAgencyCity,
            agency_state: riskAddr.countyAgencyState,
            agency_zip: riskAddr.countyAgencyZip,
        }));
    }

    window.localStorage.setItem("quote_information", JSON.stringify({
        firstYearPremium: app?.basicPolicy?.finalPremiumAmt,
        secondYearPremium: app?.lines?.[0]?.estimatedSecondYearAssessment,
        fiveYearPremium: app?.lines?.[0]?.estimatedFiveYearAveAnnualCost,
        num_bath_full: building?.numberBathrooms,
        num_bath_part: building?.numberPartialBathrooms,
        num_stories: building?.numStories,
        sqft: building?.sqFt,
        build_year: building?.yearBuilt,
        num_family: building?.numberOfFamily,
        found_type: building?.foundationType,
    }));
}

function mergeByType(targetList, sourceList, typeField) {
    (sourceList ?? []).forEach((source) => {
        const target = targetList.find((t) => t[typeField] === source[typeField]);
        if (target) Object.assign(target, source);
        else targetList.push(source);
    });
}

/** Apply a typed insured onto a fetched one in place. v5 matches parties, phones and addresses by id,
 *  so a replacement without ids is appended as a second InsuredParty and the update is rejected. */
export function mergeInsured(fetched, typed) {
    if (!fetched?.partyInfo) return typed;

    fetched.entityTypeCd = typed.entityTypeCd;
    fetched.preferredDeliveryMethod = typed.preferredDeliveryMethod;
    fetched.indexName = typed.indexName;

    typed.partyInfo.forEach((typedParty) => {
        const party = fetched.partyInfo.find((p) => p.partyTypeCd === typedParty.partyTypeCd);
        if (!party) {
            fetched.partyInfo.push(typedParty);
            return;
        }
        party.personInfo = { ...party.personInfo, ...typedParty.personInfo };
        party.nameInfo = { ...party.nameInfo, ...typedParty.nameInfo };
        party.emailInfo = { ...party.emailInfo, ...typedParty.emailInfo };
        party.phoneInfo = party.phoneInfo ?? [];
        party.addresses = party.addresses ?? [];
        mergeByType(party.phoneInfo, typedParty.phoneInfo, "phoneTypeCd");
        mergeByType(party.addresses, typedParty.addresses, "addrTypeCd");
    });

    const hasTypedJoint = typed.partyInfo.some((p) => p.partyTypeCd === "InsuredPartyJoint");
    const fetchedJoint = fetched.partyInfo.find((p) => p.partyTypeCd === "InsuredPartyJoint");
    if (!hasTypedJoint && fetchedJoint?.nameInfo) {
        fetchedJoint.nameInfo = { ...fetchedJoint.nameInfo, givenName: "", otherGivenName: "", surname: "", commercialName: "" };
    }

    return fetched;
}

// ── API functions ─────────────────────────────────────────────────────────────

export async function createQuote(quote) {
    const typed = typeof quote === 'string' ? JSON.parse(quote) : quote;
    let response;
    let app;
    await fetch(apiBase() + 'portal/createQuote', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            if (result[0]?.toString() === "ERROR") {
                response = "Service temporarily unavailable. Please try again later.";
            } else {
                app = result[0];
                storeQuoteInfo(app);
                response = "SUCCESS";
            }
        }, () => {
            response = "Service temporarily unavailable. Please try again later.";
        });

    // for an existing member InsuranceNow fills the insured from the customer record, so the typed details are applied afterwards
    if (response === "SUCCESS" && typed.customerRef) {
        app.insured = mergeInsured(app.insured, typed.insured);
        response = await updateQuote(JSON.stringify(app));
    }
    return response;
}

export async function retrieveQuote(quote) {
    let response;
    await fetch(apiBase() + 'portal/retrieveQuote', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            if (result[0]?.toString() === "ERROR") {
                response = "ERROR";
                return;
            }

            const apps = result[0]?.applications;

            if (!apps || apps.length === 0) {
                response = "ERROR";
                return;
            }

            if (apps.length > 1 && quote.QuoteNumber == undefined) {
                response = "MULTIPLE_QUOTES";
                return;
            }

            let index = 0;
            if (apps.length > 1) {
                apps.forEach((a, i) => {
                    if (a.applicationNumber === quote.QuoteNumber) index = i;
                });
            }

            const app = apps[index];
            storeQuoteInfo(app);

            const party0 = app?.insured?.partyInfo?.[0];
            const party1 = app?.insured?.partyInfo?.[1];
            const nameInfo0 = party0?.nameInfo;
            const nameInfo1 = party1?.nameInfo;

            const middleName = nameInfo0?.otherGivenName ? nameInfo0.otherGivenName + " " : "";
            const inspection_contact_name =
                (nameInfo0?.givenName || "") + " " + middleName + (nameInfo0?.surname || "");

            const billingAddr = party0?.addresses?.find(a => a.addrTypeCd === "InsuredBillingAddr");
            const mailingAddr = party0?.addresses?.find(a => a.addrTypeCd === "InsuredMailingAddr");
            const priorAddr   = party0?.addresses?.find(a => a.addrTypeCd === "InsuredPriorAddr");

            window.localStorage.setItem("personal_information", JSON.stringify({
                first_name: nameInfo0?.givenName,
                last_name: nameInfo0?.surname,
                mi: nameInfo0?.otherGivenName,
                co_applicant: false,
                co_first_name: nameInfo1?.givenName,
                co_last_name: nameInfo1?.surname,
                co_mi: nameInfo1?.otherGivenName,
                email: party0?.emailInfo?.emailAddr,
                phone: party0?.phoneInfo?.[0]?.phoneNumber,
                main_addr_street: billingAddr?.addr1,
                main_addr_city: billingAddr?.city,
                main_addr_zip: billingAddr?.postalCode,
                main_addr_state: "Virginia",
                mail_addr: false,
                mail_addr_street: mailingAddr?.addr1,
                mail_addr_city: mailingAddr?.city,
                mail_addr_zip: mailingAddr?.postalCode,
                mail_addr_state: "Virginia",
                prior_addr: false,
                prior_addr_street: priorAddr?.addr1,
                prior_addr_city: priorAddr?.city,
                prior_addr_zip: priorAddr?.postalCode,
                prior_addr_state: "Virginia",
                inspection_contact_name: inspection_contact_name.trim(),
                inspection_contact_type: "Assured",
                inspection_number: party0?.phoneInfo?.[0]?.phoneNumber,
                inspection_email: party0?.emailInfo?.emailAddr,
                inspection_pref_contact_type: "Email",
            }));

            response = "SUCCESS";
        }, () => {
            response = "ERROR";
        });
    return response;
}

export async function retrieveQuoteNumbers(quote) {
    let response;
    await fetch(apiBase() + 'portal/retrieveQuoteNumbers', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            if (result[0]?.toString() === "ERROR") {
                response = "ERROR";
            } else {
                response = result[0];
            }
        }, () => {
            response = "ERROR";
        });
    return response;
}

export async function retrieveCustomer(myData) {
    let response;
    await fetch(apiBase() + 'portal/retrieveCustomer', { method: 'POST', body: formBody(myData) })
        .then(res => res.json())
        .then(data => { response = data; })
        .catch(() => { response = "Error"; });
    return response;
}

// InsuranceNow v5 no longer returns a flat `building.namedStormDed` dollar amount (it existed in v1).
// v5 stores only the tier indicator `namedStormDedInd` (1 => 1%, 2 => 2%) and computes the tropical
// cyclone / named storm deductible for display as that percentage of Coverage A. We mirror that here.
// Confirmed against rated quotes: 200,000 x tier 1 = $2,000; 350,000 x tier 2 = $7,000.
// NOTE: tier 3 (Virginia Beach City / Accomack / Northampton) is assumed 3% — no tier-3 rated quote
// was available to verify; confirm the tier-3 percentage with underwriting if those counties are used.
export function computeNamedStormDed(building) {
    const covA = Number(building?.covALimit) || 0;
    const tier = Number(building?.namedStormDedInd) || 0;
    return Math.round((covA * tier) / 100);
}

export async function fetchQuote(quote) {
    let response;
    await fetch(apiBase() + 'portal/fetchQuote', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            // Error path returns ["ERROR"] array; success returns ApplicationFull object directly
            if (Array.isArray(result) || result?.toString() === "ERROR") {
                response = "Error";
            } else {
                response = result;
            }
        }, () => {
            response = "Error";
        });
    return response;
}

export async function updateQuote(quote) {
    let response;
    await fetch(apiBase() + 'portal/updateQuote', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            response = result[0]; // "SUCCESS", "ERROR", or a message the pages show to the user

            const app = result[1];
            if (app) {
                const building = app?.lines?.[0]?.risks?.[0]?.building;
                window.localStorage.setItem("quote_information", JSON.stringify({
                    firstYearPremium: app?.basicPolicy?.finalPremiumAmt,
                    secondYearPremium: app?.lines?.[0]?.estimatedSecondYearAssessment,
                    fiveYearPremium: app?.lines?.[0]?.estimatedFiveYearAveAnnualCost,
                    num_bath_full: building?.numberBathrooms,
                    num_bath_part: building?.numberPartialBathrooms,
                    num_stories: building?.numStories,
                    sqft: building?.sqFt,
                    build_year: building?.yearBuilt,
                    num_family: building?.numberOfFamily,
                    found_type: building?.foundationType,
                }));
            }
        }, () => {
            response = "Error";
        });
    return response;
}

export async function getToken(quote) {
    let response;
    await fetch(apiBase() + 'portal/getToken', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            response = result[0] === "SUCCESS" ? result[1].toString() : result[0];
        }, () => {
            response = "Error";
        });
    return response;
}

export async function getGoogleApiKey() {
    let response;
    await fetch(apiBase() + 'portal/getGoogleAPIKey', { method: 'GET' })
        .then(res => res.json())
        .then(result => { response = result; }, () => { response = "Error"; });
    return response;
}

export async function getLuckyOrangeSiteId() {
    let response;
    await fetch(apiBase() + 'portal/getLuckyOrangeSiteId', { method: 'GET' })
        .then(res => res.json())
        .then(result => { response = result; }, () => { response = ""; });
    return response;
}

export async function getApplicationCorrespondence(quote) {
    let response;
    await fetch(apiBase() + 'portal/getApplicationCorrespondence', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            response = result[0] === "SUCCESS" ? result[1].toString() : result[0];
        }, () => {
            response = "Error";
        });
    return response;
}

export function createActivityQuoteDB(page_name, activity_code, description) {
    fetch(apiBase() + 'portal/createActivityQuoteDB', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
            Quote_number: localStorage.getItem('quote_number'),
            Page_Name: page_name,
            Activity_Code: activity_code,
            Source_IP: localStorage.getItem('user_ip'),
            Session_ID: localStorage.getItem('session_id'),
            Description: description,
        })
    });
}

export async function createNewLandingPageQuote(quote) {
    let response;
    await fetch(apiBase() + 'portal/createQuote', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            if (result[0]?.toString() === "ERROR") {
                response = "Service temporarily unavailable. Please try again later.";
            } else {
                const app = result[0];
                window.localStorage.setItem("quote_number", app.applicationNumber);
                window.localStorage.setItem("quote_system_id", app.systemId?.toString());
                window.localStorage.setItem("cov_a", app?.lines?.[0]?.risks?.[0]?.building?.covALimit);

                try {
                    const riskAddr = app?.lines?.[0]?.risks?.[0]?.building?.addresses
                        ?.find(a => a.addrTypeCd === 'RiskLookupAddr');
                    if (riskAddr) {
                        window.localStorage.setItem("agency_information", JSON.stringify({
                            agency_name: riskAddr.countyAgencyName,
                            agency_phone: riskAddr.countyAgencyPhoneNumber,
                            agency_email: riskAddr.countyAgencyEmail,
                            agency_addres: riskAddr.countyAgencyAddress,
                            agency_city: riskAddr.countyAgencyCity,
                            agency_state: riskAddr.countyAgencyState,
                            agency_zip: riskAddr.countyAgencyZip,
                        }));
                    }
                } catch (e) {
                    console.log("Error fetching agency information");
                }

                try {
                    const line = app?.lines?.[0];
                    window.localStorage.setItem("fiveYearSavingsLow",  String(line?.estimatedFiveYearSavingsLow  ?? ""));
                    window.localStorage.setItem("fiveYearSavingsHigh", String(line?.estimatedFiveYearSavingsHigh ?? ""));
                    window.localStorage.setItem("tenYearSavingsLow",   String(line?.estimatedTenYearSavingsLow  ?? ""));
                    window.localStorage.setItem("tenYearSavingsHigh",  String(line?.estimatedTenYearSavingsHigh ?? ""));
                    window.localStorage.setItem("annualAssessment",    String(line?.estimatedAnnualAssessment   ?? ""));
                    window.localStorage.setItem("estimatedPremium",    String(line?.estimatedPremium            ?? ""));
                } catch (e) {
                    console.log("Error fetching premium information");
                }

                window.localStorage.setItem("quote_information", JSON.stringify({
                    firstYearPremium: app?.basicPolicy?.finalPremiumAmt,
                    secondYearPremium: app?.lines?.[0]?.estimatedSecondYearAssessment,
                    fiveYearPremium: app?.lines?.[0]?.estimatedFiveYearAveAnnualCost,
                }));

                window.localStorage.setItem("personal_information", JSON.stringify({
                    customer_ref: app.customerRef,
                }));

                response = "SUCCESS";
            }
        }, () => {
            response = "Service temporarily unavailable. Please try again later.";
        });
    return response;
}

export async function updateNewLandingPageQuote(quote) {
    let response;
    await fetch(apiBase() + 'portal/updateQuote', { method: 'POST', body: formBody(quote) })
        .then(res => res.json())
        .then(result => {
            response = result[0]; // "SUCCESS", "ERROR", or a message the pages show to the user

            const app = result[1];
            if (app) {
                window.localStorage.setItem("quote_information", JSON.stringify({
                    firstYearPremium: app?.basicPolicy?.finalPremiumAmt,
                    secondYearPremium: app?.lines?.[0]?.estimatedSecondYearAssessment,
                    fiveYearPremium: app?.lines?.[0]?.estimatedFiveYearAveAnnualCost,
                }));
            }
        }, () => {
            response = "Error";
        });
    return response;
}
